import React from 'react';
import { Modal, Collapse, Button } from 'antd';
import { ExclamationCircleOutlined, FileExcelOutlined } from '@ant-design/icons';
import { checkItemStatus as statusAPI, deactivateItem } from 'services/settings/status';

const { Panel } = Collapse;

/**
 * Helper to export data to CSV (Excel compatible)
 */
const exportToExcel = (data, itemLabel) => {
	if (!data || data.length === 0) return;

	const headers = ['Module Name', 'Item Name'];
	const rows = data.map(dep => [
		`"${(dep.modulename || '').replace(/"/g, '""')}"`,
		`"${(dep.itemname || '').replace(/"/g, '""')}"`
	]);

	const csvContent = [
		headers.join(','),
		...rows.map(row => row.join(','))
	].join('\n');

	const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	const timestamp = new Date().getTime();
	
	link.href = url;
	link.setAttribute('download', `${itemLabel}_dependencies_${timestamp}.csv`);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

/**
 * Handles the complete deactivation validation workflow: 
 * 1. Checks for dependencies via API.
 * 2. If dependencies found, shows a confirmation modal informing the user that 
 *    proceeding will clean up/delete those dependencies.
 * 3. If NO dependencies, calls onConfirm immediately (no modal).
 * 
 * @param {string} itemType - 'Exam Request', 'Exam Result', etc.
 * @param {number} itemId - ID of the item.
 * @param {object} options - { title, itemLabel, onConfirm, onCancel }
 *                             onConfirm(isForce) - isForce is true if forced deactivation (dependencies exist)
 */
export default async function showDeactivationModal(itemType, itemId, {
	title,
	itemLabel = 'item',
	onConfirm,
	onCancel
}) {
	const dependencies = await statusAPI(itemType, itemId);

	if (dependencies && dependencies.length > 0) {
		const actions = dependencies.map(dep => (dep.tableaction || '').toUpperCase());
		const hasDelete = actions.some(a => a.includes('DELETE'));
		const hasUpdate = actions.some(a => a.includes('UPDATE'));

		let okText = 'Deactivate Anyway';
		let actionLabel = 'remove or update';

		if (hasDelete && hasUpdate) {
			okText = 'Delete/Update Anyway';
			actionLabel = 'remove and update';
		} else if (hasDelete) {
			okText = 'Delete Anyway';
			actionLabel = 'remove';
		} else if (hasUpdate) {
			okText = 'Update Inactive';
			actionLabel = 'update';
		}

		// Group dependencies by modulename
		const groupedDeps = (dependencies || []).reduce((acc, dep) => {
			const module = dep.modulename || 'Others';
			if (!acc[module]) acc[module] = [];
			acc[module].push(dep.itemname);
			return acc;
		}, {});

		Modal.confirm({
			title: `Deactivate ${itemLabel}?`,
			icon: <ExclamationCircleOutlined />,
			content: (
				<div style={{ marginTop: 20, position: 'relative' }}>
					<p>This {itemLabel} is currently in use in the following:</p>
					
					<Collapse accordion ghost defaultActiveKey={Object.keys(groupedDeps)[0]}>
						{Object.entries(groupedDeps).map(([module, items], index) => (
							<Panel 
								header={<span><strong>{module}</strong> ({items.length})</span>} 
								key={module}
							>
								<ul style={{ margin: 0, paddingLeft: '20px', maxHeight: '150px', overflowY: 'auto' }}>
									{items.map((itemName, i) => (
										<li key={i}>{itemName}</li>
									))}
								</ul>
							</Panel>
						))}
					</Collapse>

					<p style={{ marginTop: 15, fontWeight: 'bold', color: '#ff4d4f' }}>
						Warning: Proceeding will automatically {actionLabel} this {itemLabel} from the records listed above.
					</p>
					<p>Do you want to proceed with deactivation?</p>

					<div style={{ position: 'absolute', bottom: -52, left: -45 }}>
						<Button 
							type="link" 
							size="small" 
							icon={<FileExcelOutlined />}
							onClick={() => exportToExcel(dependencies, itemLabel)}
							style={{ display: 'flex', alignItems: 'center' }}
						>
							Download to Excel
						</Button>
					</div>
				</div>
			),
			onOk: () => {
				onConfirm(true); // Signal that this is a forced deactivation
			},
			onCancel: onCancel, // Revert switch if user cancels
			centered: true,
			width: 600,
			okText: okText,
			cancelText: 'Cancel',
			okButtonProps: { danger: true },
		});
	} else {
		// If no dependencies, we proceed normally
		onConfirm(false); // Signal that this is a normal deactivation
	}
}
